import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { PHONE_RAW } from "@/i18n/translations";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const { lang, toggle, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "about", label: t.nav.about },
    { id: "gallery", label: t.nav.gallery },
    { id: "perf", label: t.nav.perf },
    { id: "hotel", label: t.nav.hotel },
    { id: "events", label: t.nav.events },
    { id: "reserve", label: t.nav.reserve },
    { id: "careers", label: t.nav.careers },
    { id: "contact", label: t.nav.contact },
  ];

  const go = (id) => {
    scrollTo(id);
    setOpen(false);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-obsidian/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="logo-btn"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2 group"
        >
          <span className="font-display text-lg md:text-xl font-700 tracking-tight text-white">
            VIP<span className="text-brand">1</span>
          </span>
          <span className="hidden sm:block font-body text-[10px] tracking-[0.35em] uppercase text-white/50 mt-1">
            Night Club
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-6">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="font-body text-sm text-white/70 hover:text-white transition-colors relative group whitespace-nowrap"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-brand transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="font-display text-xs tracking-widest text-white/80 hover:text-brand transition-colors border border-white/15 hover:border-brand/60 px-3 py-1.5"
          >
            {lang === "tr" ? "EN" : "TR"}
          </button>
          <a
            data-testid="nav-call"
            href={`tel:+${PHONE_RAW}`}
            className="hidden md:inline-flex items-center gap-2 bg-brand hover:bg-brand-light text-white font-body text-sm px-6 py-2.5 transition-colors"
          >
            <Phone size={15} /> {t.nav.book}
          </a>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-white"
            aria-label="menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-obsidian/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mnav-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left font-heading text-2xl text-white/85 hover:text-brand transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={`tel:+${PHONE_RAW}`}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-brand text-white font-body py-3"
              >
                <Phone size={16} /> {t.nav.book}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
